/* eslint-disable no-console */

import type { LogLevel } from './logLevel';

import { resolveLogLevel } from './logLevel';

const isServer = typeof window === 'undefined';

// Client-side logger using console with pino-like levels
const CLIENT_LOG_LEVELS = {
  debug: 20,
  info: 30,
  warn: 40,
  error: 50,
} as const;

const currentLogLevel =
  CLIENT_LOG_LEVELS[resolveLogLevel() as keyof typeof CLIENT_LOG_LEVELS] || CLIENT_LOG_LEVELS.debug;

const createClientMethod =
  (consoleMethod: (...args: unknown[]) => void, methodLevel: number) =>
  (...args: unknown[]) => {
    if (methodLevel >= currentLogLevel) {
      consoleMethod(...args);
    }
  };

const clientLogger = {
  debug: createClientMethod(console.debug, CLIENT_LOG_LEVELS.debug),
  info: createClientMethod(console.info, CLIENT_LOG_LEVELS.info),
  warn: createClientMethod(console.warn, CLIENT_LOG_LEVELS.warn),
  error: createClientMethod(console.error, CLIENT_LOG_LEVELS.error),
};

// Server-side logger - only loaded when on server
let serverLoggerInstance: unknown = null;

const loadPinoLogger = async () => {
  if (!isServer) {
    console.error('Pino logger can only be loaded on server side');

    return null;
  }

  const { createPinoLogger } = await import('./pino');

  return createPinoLogger();
};

const createServerMethod =
  (methodName: LogLevel) =>
  async (...args: unknown[]) => {
    if (!serverLoggerInstance) {
      serverLoggerInstance = await loadPinoLogger();
    }

    (serverLoggerInstance as Record<string, (...args: unknown[]) => void>)?.[methodName]?.(...args);
  };

const serverLogger = {
  debug: createServerMethod('debug'),
  info: createServerMethod('info'),
  warn: createServerMethod('warn'),
  error: createServerMethod('error'),
};

export const logger = isServer ? serverLogger : clientLogger;

export default logger;
